/**
 * R-TISCALIIT operator panel data layer (single page, 2026-09-16).
 *
 * Tiscaliit does NOT mint aliases: one sellable unit per mailbox, and the scarce
 * resource is the (mailbox, site) right. ONE endpoint feeds the whole page:
 * stock counts, credential-probe state, per-site sales and the per-mailbox
 * detail ("hangi siteler için satıldı").
 *
 * Endpoint: GET /admin/providers/tiscaliit/site-usage (admin-guarded; served only
 * for a bundle that declares siteScopedRights — mail.com/gmx answer 404
 * provider_not_supported).
 */
import { callAdminApi, resolveBaseUrl, type ApiResult } from '../../api/client.js';

export interface TiscaliitStockUnits {
  readonly total: number;
  readonly available: number;
  readonly leased: number;
  readonly reserved: number;
  readonly burnedPairs: number;
  readonly mailboxesWithBurns: number;
}

export interface TiscaliitProbeState {
  /** login verified at least once (sellable) */
  readonly verified: number;
  /** imported, not yet probed (becomes sellable within ~1 min per batch) */
  readonly pending: number;
  /** login refused; re-probed after the backoff, sticky after 10 attempts */
  readonly quarantined: number;
}

export interface TiscaliitSiteUsageRow {
  readonly site: string;
  /** mail arrived — permanent, closed for everyone */
  readonly burnedMailboxes: number;
  /** sold but no mail — closed only for that customer */
  readonly servedMailboxes: number;
  /** every mailbox ever sold for this site (burned + served) */
  readonly soldMailboxes: number;
  /** burnedMailboxes / units.total × 100 — "stokların %kaçı bu site için". */
  readonly shareOfStock: number;
  readonly lastBurnedAt: string | null;
  readonly lastSoldAt: string | null;
}

export interface TiscaliitMailboxRow {
  readonly address: string;
  readonly unitStatus: string;
  readonly accountStatus: string;
  readonly sessionState: string;
  readonly verified: boolean;
  readonly hasCredential: boolean;
  readonly consecutiveErrors: number;
  readonly cooldownUntil: string | null;
  readonly lastSeenAt: string | null;
  readonly burnedSites: readonly string[];
  readonly servedSites: readonly string[];
  readonly lastBurnedAt: string | null;
  readonly lastServedAt: string | null;
}

export interface TiscaliitSiteUsage {
  readonly providerId: string;
  readonly units: TiscaliitStockUnits;
  readonly accounts: number;
  readonly probe?: TiscaliitProbeState;
  readonly sites: readonly TiscaliitSiteUsageRow[];
  /** Never sent for view=summary (the list is the manager's paged view). */
  readonly mailboxes?: readonly TiscaliitMailboxRow[];
}

/**
 * The page header's KPIs only: `?view=summary` (2026-10-23) answers units,
 * accounts, probe and sites WITHOUT the mailbox list. The list is paged by
 * the shared <MailboxUnitManager> (?view=mailboxes); gmxbox's 66K mailboxes
 * used to ride along here as a ~27 MB payload and froze the browser.
 */
export async function fetchTiscaliitSiteUsage(
  signal?: AbortSignal,
): Promise<ApiResult<TiscaliitSiteUsage>> {
  return callAdminApi<TiscaliitSiteUsage>(
    { baseUrl: resolveBaseUrl(), signal },
    { path: '/admin/providers/tiscaliit/site-usage?view=summary' },
  );
}

/** Pure helper (unit-tested): share of a partial stock count, one decimal. */
export function sharePercent(part: number, total: number): number {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}

/** Sellable share of the whole stock, one decimal. */
export function availableShare(units: TiscaliitStockUnits): number {
  return sharePercent(units.available, units.total);
}

/** Operator-facing label for a mailbox's credential/probe state. */
export function mailboxStateLabel(m: TiscaliitMailboxRow): {
  readonly text: string;
  readonly tone: 'success' | 'warn' | 'danger' | 'neutral';
} {
  if (m.accountStatus === 'login_failed') return { text: 'Karantina (giriş reddedildi)', tone: 'danger' };
  if (m.accountStatus !== 'active') return { text: m.accountStatus, tone: 'neutral' };
  if (!m.hasCredential) return { text: 'Kimlik yok', tone: 'danger' };
  if (m.verified) return { text: 'Doğrulandı', tone: 'success' };
  return { text: 'Doğrulama bekliyor', tone: 'warn' };
}
